/**
 * AGROMX Frontend - Tracking Service
 * Servicio de tracking GPS para vehículos
 */

import api from './api.js';

class TrackingService {
  /**
   * Obtener estadísticas de tracking
   */
  async getStats() {
    return api.get('/tracking/stats');
  }

  /**
   * Listar vehículos con última posición
   * @param {object} params - { page, limit, search, estado }
   */
  async getVehiculos(params = {}) {
    return api.get('/tracking/vehiculos', params);
  }

  /**
   * Obtener detalle de un vehículo
   * @param {number} id - ID del vehículo
   */
  async getVehiculoDetalle(id) {
    return api.get(`/tracking/vehiculos/${id}`);
  }

  /**
   * Obtener recorridos históricos de un vehículo
   */
  async getRecorridos(id, fechaInicio, fechaFin) {
    return api.get(`/tracking/vehiculos/${id}/recorridos`, {
      fecha_inicio: fechaInicio,
      fecha_fin: fechaFin
    });
  }

  /**
   * Obtener eventos de un vehículo
   */
  async getEventos(id, fechaInicio, fechaFin) {
    return api.get(`/tracking/vehiculos/${id}/eventos`, {
      fecha_inicio: fechaInicio,
      fecha_fin: fechaFin
    });
  }

  /**
   * Obtener alertas de un vehículo
   */
  async getAlertas(id, fechaInicio, fechaFin) {
    return api.get(`/tracking/vehiculos/${id}/alertas`, {
      fecha_inicio: fechaInicio,
      fecha_fin: fechaFin
    });
  }

  /**
   * Obtener datos históricos de sensores para gráficos
   */
  async getSensoresHistorico(id, fechaInicio, fechaFin) {
    return api.get(`/tracking/vehiculos/${id}/sensores-historico`, {
      fecha_inicio: fechaInicio,
      fecha_fin: fechaFin
    });
  }

  /**
   * Listar distribuidores con total de vehículos asignados (solo admin)
   */
  async getDistribuidores() {
    return api.get('/tracking/distribuidores');
  }

  /**
   * Obtener notificaciones desde la base de datos.
   * since es opcional: sin valor devuelve el historial reciente.
   */
  async getNotificacionesNuevas(since) {
    const params = since ? { since } : {};
    return api.get('/tracking/notificaciones/nuevas', params);
  }

  /**
   * Formatear fecha/hora para mostrar
   */
  formatFechaHora(dateString) {
    if (dateString == null || String(dateString).trim() === '') return 'Sin datos';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return 'Sin datos';
    return date.toLocaleString('es-MX', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }

  /**
   * Obtener clase CSS para badge de estado
   */
  getEstadoBadgeClass(ignicion) {
    return ignicion ? 'vehicle-status-on' : 'vehicle-status-off';
  }

  /**
   * Obtener texto de estado
   */
  getEstadoTexto(ignicion, velocidad) {
    if (!ignicion) return 'Apagado / Detenido';
    if (velocidad > 0) return 'Encendido / Movimiento';
    return 'Encendido / Detenido';
  }

  /**
   * Etiqueta principal en tracking: numero de serie del catalogo; si no hay, nombre RedGPS
   */
  etiquetaVehiculo(v) {
    if (!v) return '';
    if (v.num_serie != null && String(v.num_serie).trim() !== '') return String(v.num_serie).trim();
    return v.nombre_activo || '';
  }

  /**
   * Estado real del motor (3 estados) para UI: encendido / apagado / trampa de ignicion
   * (unidad trabajando con el switch de ignicion apagado). Fuente de verdad: columna
   * redgps_ubicaciones.estado_motor, calculada por el webhook.
   * Colores: encendido azul, apagado gris, trampa ambar (independiente de antiguedad).
   * @param {string} estadoMotor - 'encendido' | 'apagado' | 'trampa_ignicion'
   * @returns {{texto: string, claseCss: string, color: string}}
   */
  getEstadoMotorInfo(estadoMotor) {
    switch (estadoMotor) {
      case 'encendido':
        return { texto: 'Encendido', claseCss: 'vehicle-status-on', color: '#2563eb' };
      case 'trampa_ignicion':
        return { texto: 'Trabajando con switch apagado', claseCss: 'vehicle-status-trampa', color: '#f59e0b' };
      case 'apagado':
      default:
        return { texto: 'Apagado', claseCss: 'vehicle-status-off', color: '#6b7280' };
    }
  }

  /**
   * Antiguedad del ultimo reporte GPS (independiente del estado del motor).
   * gris = sin datos | verde < 1 dia | amarillo 1-10 dias | rojo > 10 dias
   * @param {string|Date|null} fechaHora
   * @returns {{nivel: string, color: string, texto: string}}
   */
  getEstadoReporteInfo(fechaHora) {
    if (fechaHora == null || String(fechaHora).trim() === '') {
      return { nivel: 'sin_datos', color: '#9ca3af', texto: 'Sin datos' };
    }
    const d = new Date(fechaHora);
    if (Number.isNaN(d.getTime())) {
      return { nivel: 'sin_datos', color: '#9ca3af', texto: 'Sin datos' };
    }
    const horas = (Date.now() - d.getTime()) / (1000 * 60 * 60);
    if (horas < 0) {
      // Fecha futura por desfase de reloj: tratar como reciente
      return { nivel: 'reciente', color: '#22c55e', texto: 'Localizado < 1 dia' };
    }
    if (horas < 24) {
      return { nivel: 'reciente', color: '#22c55e', texto: 'Localizado < 1 dia' };
    }
    if (horas <= 240) {
      // 1 a 10 dias (240 h)
      return { nivel: 'medio', color: '#f59e0b', texto: '1-10 dias sin reporte' };
    }
    return { nivel: 'antiguo', color: '#ef4444', texto: '> 10 dias sin reporte' };
  }
}

export default new TrackingService();
